import React from 'react';
import { toast } from 'react-hot-toast';

import {
  useTaskProcessingStore,
  selectProcessingTasks,
  isTaskTerminal,
} from '../../stores/taskProcessing.store';
import type { ProcessingTask } from '../../stores/taskProcessing.types';

const STATUS_MESSAGES: Record<ProcessingTask['normalizedStatus'], string> = {
  queued: 'Tarea en cola',
  processing: 'Procesando tarea',
  retrying: 'Reintentando tarea',
  success: 'Tarea completada',
  failure: 'Tarea fallida',
  canceled: 'Tarea cancelada',
};

interface TaskNotificationsProps {
  suppressAuthFailures?: boolean;
}

export const TaskNotifications: React.FC<TaskNotificationsProps> = ({
  suppressAuthFailures = true,
}) => {
  const tasks = useTaskProcessingStore(selectProcessingTasks);
  const seenRef = React.useRef<Map<string, ProcessingTask['normalizedStatus']>>(
    new Map(),
  );
  const toastRef = React.useRef<Map<string, string>>(new Map());

  React.useEffect(() => {
    tasks.forEach((task) => {
      const previousStatus = seenRef.current.get(task.id);
      const currentStatus = task.normalizedStatus;

      if (previousStatus === currentStatus) {
        return;
      }

      seenRef.current.set(task.id, currentStatus);

      if (!isTaskTerminal(task)) {
        return;
      }

      if (suppressAuthFailures && task.error && task.error.includes('401')) {
        return;
      }

      const message = task.error ?? STATUS_MESSAGES[currentStatus];

      const existingToastId = toastRef.current.get(task.id);

      if (existingToastId) {
        toast.dismiss(existingToastId);
        toastRef.current.delete(task.id);
      }

      const nextToastId = `task-${task.id}`;

      switch (currentStatus) {
        case 'success':
          toast.success(message ?? 'Tarea completada', {
            id: nextToastId,
          });
          toastRef.current.set(task.id, nextToastId);
          break;
        case 'failure':
        case 'canceled':
          toast.error(message ?? 'Tarea finalizada con errores', {
            id: nextToastId,
          });
          toastRef.current.set(task.id, nextToastId);
          break;
        default:
          toastRef.current.delete(task.id);
          break;
      }
    });

    return () => {
      toastRef.current.forEach((id) => {
        toast.dismiss(id);
      });
      toastRef.current.clear();
    };
  }, [tasks, suppressAuthFailures]);

  return null;
};

TaskNotifications.displayName = 'TaskNotifications';
